import { REPORTS_STATUS } from '@aca-new/app/pages/reports/shared/constants/report-status.constants';
import { ETableDataType } from '@aca-new/app/shared/components/table/shared/enums/table-data-type.enum';
import { ETableLinkAppearance } from '@aca-new/app/shared/components/table/shared/enums/table-link-appearance.enum';
import { ITableBodyCell } from '@aca-new/app/shared/components/table/shared/interfaces/table-data.interface';
import { ITableHeader } from '@aca-new/app/shared/components/table/shared/interfaces/table-header.interface';
import { resolveTableColumnWidth } from '@aca-new/app/shared/components/table/shared/utils/table-column-width.util';
import { QimaDotService } from '@aca-new/app/shared/services/exported-services/qima-dot/qima-dot.service';
import { QimaIconService } from '@aca-new/app/shared/services/exported-services/qima-icon/qima-icon.service';
import { QimaTooltipService } from '@aca-new/app/shared/services/exported-services/qima-tooltip/qima-tooltip.service';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { EQimaButtonIconStyle, EQimaIconName, EQimaPosition, QimaOptionalType } from '@qima/ngx-qima';

@Component({
  standalone: false,
  selector: 'app-common-table-cell',
  templateUrl: './common-table-cell.component.html',
  styleUrls: ['./common-table-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonTableCellComponent {
  /**
   * @description
   * the data of the cell
   * @type {ITableBodyCell}
   * @default undefined
   */
  @Input('commonTableCellData')
  public cellData: QimaOptionalType<ITableBodyCell> = undefined;

  /**
   * @description
   * the origin data of the cell
   * @type {QimaOptionalType<unknown>}
   * @default undefined
   */
  @Input('commonTableCellOriginData')
  public originData: QimaOptionalType<unknown> = undefined;

  /**
   * @description
   * the width of the cell
   * @type {string}
   * @default 'auto'
   */
  @Input('commonTableCellWidth')
  public width: string = 'auto';

  /**
   * @description
   * the data of the index
   * @type {number}
   * @default 0
   */
  @Input('commonTableCellIndex')
  public cellIndex: number = 0;

  /**
   * @description
   * the header configuration for this cell
   * @type {QimaOptionalType<ITableHeader>}
   * @default undefined
   */
  @Input('commonTableCellHeader')
  public header: QimaOptionalType<ITableHeader> = undefined;

  /**
   * @description Resolved CSS width (px, %, or other unit) for this cell.
   * @type {string | null}
   */
  public get resolvedTableCellWidth(): string | null {
    return resolveTableColumnWidth(this.width);
  }

  /**
   * @returns {boolean} Whether the LINK cell uses chevron presentation.
   */
  public get isLinkChevronAppearance(): boolean {
    return (this.cellData?.linkAppearance ?? ETableLinkAppearance.UNDERLINE) === ETableLinkAppearance.CHEVRON;
  }

  // TODO this output need redesign to solution multi icon click
  @Output('commonTableIconCellClick')
  public readonly iconCellClick: EventEmitter<number> = new EventEmitter<number>();

  @Output('commonTableDecisionApprove')
  public readonly decisionApprove: EventEmitter<unknown> = new EventEmitter<unknown>();

  @Output('commonTableDecisionReject')
  public readonly decisionReject: EventEmitter<unknown> = new EventEmitter<unknown>();

  public dataType = ETableDataType;
  public linkAppearance = ETableLinkAppearance;
  public linkChevronIcon = EQimaIconName.CHEVRON_RIGHT;
  public statusTooltipPosition = EQimaPosition.LEFT;
  public statusType = REPORTS_STATUS;
  public isHovered = false;
  public iconStyleSuccess = EQimaButtonIconStyle.SECONDARY_SUCCESS;
  public iconStyleFail = EQimaButtonIconStyle.SECONDARY_DESTRUCTIVE;

  public constructor(
    public readonly qimaTooltip: QimaTooltipService,
    public readonly qimaDot: QimaDotService,
    public readonly qimaIcon: QimaIconService,
    private readonly _changeDetectorRef: ChangeDetectorRef
  ) {}

  public onIconCellClick(): void {
    this.iconCellClick.emit(this.cellIndex);
  }

  public onDecisionApprove(event: Event): void {
    event.stopPropagation();

    if (this.originData) {
      this.decisionApprove.emit(this.originData);
    }
  }

  public onDecisionReject(event: Event): void {
    event.stopPropagation();

    if (this.originData) {
      this.decisionReject.emit(this.originData);
    }
  }

  public onMouseEnter(): void {
    if (this._shouldShowDecisionButtons()) {
      this.isHovered = true;
      this._changeDetectorRef.markForCheck();
    }
  }

  public onMouseLeave(): void {
    this.isHovered = false;
    this._changeDetectorRef.markForCheck();
  }

  private _shouldShowDecisionButtons(): boolean {
    return this.header?.enablePendingDecisionButtons === true && this.cellData?.type === ETableDataType.DOT_LABEL && this.cellData?.label === this.statusType.pending;
  }
}
